import { markRaw } from 'vue';
import { Basemap, CommonMapAPI, GraphicLayer, InstanceAPI } from '@/api/internal';
import { Graphic, LayerType, PolygonStyle } from '@/geo/api';
import type { Extent, RampMapConfig } from '@/geo/api';
import { EsriAPI, EsriWatch } from '@/geo/esri';
import type { EsriExtent, EsriScreenPoint, EsriViewDragEvent } from '@/geo/esri';

import { useConfigStore } from '@/stores/config';
import { useOverviewmapStore } from '@/fixtures/overviewmap/store';

export class OverviewMapAPI extends CommonMapAPI {
    protected overviewGraphicLayer: GraphicLayer;
    overviewmapStore: any;

    /**
     * @constructor
     * @param {InstanceAPI} iApi the RAMP instance
     */
    constructor(iApi: InstanceAPI) {
        super(iApi, 'overview');
        this.overviewGraphicLayer = this.$iApi.geo.layer.createLayer({
            id: 'RampOverviewGraphic',
            layerType: LayerType.GRAPHIC,
            url: '',
            cosmetic: true,
            system: true
        }) as GraphicLayer;
        this.overviewmapStore = useOverviewmapStore(this.$vApp.$pinia);
    }

    /**
     * Will generate a ESRI map view and add it to the page
     * Must provide the basemap or basemap id to be used when creating the map view
     *
     * @param {string | Basemap} basemap the id of the basemap that should be used when creating the map view
     * @returns {Promise} resolves when the map view has been created.
     * @protected
     */
    protected async createMapView(basemap: string | Basemap): Promise<void> {
        if (!basemap) {
            throw new Error('Attempted to create overview map view without a basemap');
        }

        const bm: Basemap = typeof basemap === 'string' ? await this.findBasemap(basemap) : basemap;

        await this.applyBasemap(bm);

        // TODO: This assumes that the overview map will be synced with the main map's tile schema, so it just uses the extent/sr from the main map
        //       Revisit this when enhancing the overview map to be able to use a different tile schema than the main map
        this._rampExtentSet = this.$iApi.geo.map.getExtentSet().clone();
        this._rampSR = this._rampExtentSet.sr.clone();

        const expandFactor: number = this.overviewmapStore.expandFactor;

        // create esri view with config
        this.esriView = markRaw(
            await EsriAPI.MapView({
                map: this.esriMap,
                container: this._targetDiv,
                constraints: {
                    rotationEnabled: false
                },
                attributionVisible: false,
                spatialReference: this._rampSR.toESRI(),
                extent: this.$iApi.geo.map.getExtent().toESRI().expand(expandFactor) // use the expanded main map extent
            })
        );

        // Remove all ui components
        this.esriView.ui.components = [];

        this.handlers.push({
            type: 'mouse-wheel',
            handler: this.esriView.on('mouse-wheel', esriMouseWheel => {
                esriMouseWheel.stopPropagation();
            })
        });

        this.handlers.push({
            type: 'double-click',
            handler: this.esriView.on('double-click', esriDoubleClick => {
                esriDoubleClick.stopPropagation();
            })
        });

        this.handlers.push({
            type: 'key-down',
            handler: this.esriView.on('key-down', esriKeyDown => {
                esriKeyDown.stopPropagation();
            })
        });

        this.handlers.push({
            type: 'key-up',
            handler: this.esriView.on('key-up', esriKeyUp => {
                esriKeyUp.stopPropagation();
            })
        });

        this.handlers.push({
            type: 'drag',
            handler: this.esriView.on('drag', esriDrag => {
                esriDrag.stopPropagation();
                this.mapDrag(esriDrag);
            })
        });

        this.esriView!.container!.addEventListener('touchmove', e => {
            // need this for panning and zooming to work on mobile devices / touchscreens
            // touchmove stops the drag event (what the MapView reacts to) from firing properly
            e.preventDefault();
        });

        // most browsers have a webgl context limit of 16 (one instance of RAMP can use 2 - map and overview map).
        // once the number of contexts is higher than the limit, the oldest context will be lost.
        // when instance is visible on screen, if its overview context is lost then recover it.
        EsriWatch(
            () => this.esriView!.fatalError,
            () => {
                const observer = new IntersectionObserver(entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            this.esriView?.tryFatalErrorRecovery();
                            observer.disconnect();
                        }
                    });
                });
                observer.observe(this.esriView!.container!);
            }
        );

        // as of ESRI v4.26, we need to marinate until .when() is done.
        // otherwise, something happens too fast and the initial calls to view.goTo() grouse quite a lot,
        // and ends up breaking the overview.
        this.esriView.when(() => {
            this._viewPromise.resolveMe();
            this.created = true;
            this.loadDefProm.resolveMe();
        });
    }

    async addMapGraphicLayer() {
        if (!this.esriMap) {
            this.noMapErr();
            return;
        }

        const overviewGraphic = new Graphic(this.$iApi.geo.map.getExtent(), 'overview-graphic');

        const borderColour = this.overviewmapStore.borderColour ?? '#FF0000';
        const borderWidth = this.overviewmapStore.borderWidth ?? 1;
        const areaColour = this.overviewmapStore.areaColour ?? '#000000';
        const areaOpacity = this.overviewmapStore.areaOpacity ?? 0.25;

        // generate hex colour with alpha
        const areaFill = `${areaColour}${Math.round(areaOpacity * 255).toString(16)}`;

        overviewGraphic.style = new PolygonStyle({
            fill: { colour: areaFill },
            outline: {
                colour: borderColour,
                width: borderWidth
            }
        });

        await this.overviewGraphicLayer.initiate();
        await this.overviewGraphicLayer.addGraphic(overviewGraphic);
        this.esriMap?.add(this.overviewGraphicLayer.esriLayer!);
    }

    async removeMapGraphicLayer() {
        if (!this.esriMap) {
            this.noMapErr();
            return;
        }

        if (!this.overviewGraphicLayer.esriLayer) {
            throw new Error(
                'Attempted to remove layer from the map without an esri layer. Likely layer.initiate() was not called or had not finished.'
            );
        }

        this.overviewGraphicLayer.removeGraphic();
        this.esriMap.remove(this.overviewGraphicLayer.esriLayer);

        // This removes the reference to .esriLayer so must happen after the esriMap.remove()
        await this.overviewGraphicLayer.terminate();
    }

    /**
     * Destroys the ESRI map view
     *
     * @protected
     */
    protected destroyMapView(): void {
        // override the method to remove this listener
        this.esriView?.container!.removeEventListener('touchmove', e => {
            e.preventDefault();
        });
        super.destroyMapView();
    }

    /**
     * Searches the local basemap list, then the main map basemaps for a basemap with the given id
     * Throws error if basemap could not be found
     *
     * @param {string} id basemap id
     * @returns {Promise<Basemap>} resolves with the found basemap
     * @protected
     */
    async findBasemap(id: string): Promise<Basemap> {
        const bm: Basemap | undefined = this._basemapStore.find(bms => bms.id === id);
        if (bm) {
            return bm;
        } else {
            // search the basemaps in the main map config
            const configStore = useConfigStore(this.$vApp.$pinia);
            const mainMapConfig: RampMapConfig = configStore.config.map;
            if (mainMapConfig) {
                const bmConfig = mainMapConfig.basemaps.find(bm => bm.id === id);
                if (bmConfig) {
                    return Basemap.create(bmConfig);
                }
            }
        }

        throw new Error(`Invalid basemap id requested: ${id}`);
    }

    /**
     * Sets the overview map's basemap to the basemap with the given id.
     * Will refresh the map view if set basemap uses different tile schema.
     *
     * Should only be called by the overview map component
     *
     * @param {string} basemapId the basemap id
     * @returns {Promise<boolean>} resolves with boolean indicates if the schema has changed
     */
    async setBasemap(basemapId: string): Promise<boolean> {
        if (!this.esriView || !this.esriMap) {
            this.noMapErr();
            return false;
        }

        const newBm = await this.findBasemap(basemapId);

        // get the current basemap
        const currBm = this.getCurrentBasemapId() ? await this.findBasemap(this.getCurrentBasemapId()!) : undefined;

        const differentSchema = currBm?.tileSchemaId !== newBm.tileSchemaId;

        if (differentSchema) {
            this.destroyMapView();
            await this.createMapView(newBm);
        } else {
            await this.applyBasemap(newBm);
        }

        return differentSchema;
    }

    /**
     * Initial esri extent of graphic during drag
     *
     * @private
     */
    private startExtent: EsriExtent | null = null;

    /**
     * Moves graphic and zooms main map if extent graphic is dragged
     *
     * @param {EsriViewDragEvent} esriDrag
     * @private
     */
    private async mapDrag(esriDrag: EsriViewDragEvent) {
        //@ts-expect-error .native is either PointerEvent or MouseEvent. Complaint is that MouseEvent has no .pointerType. Which appears true according to MDN. But when we do a mouse click, it has the prop. It might be that using a mouse generates a pointer event, not a mouse event.
        if (esriDrag.native.pointerType === 'mouse') {
            if (esriDrag.action === 'start') {
                // check if drag hits graphic, if so set start extent
                if (await this.cursorHitTest(esriDrag)) {
                    this.startExtent = markRaw(
                        this.overviewGraphicLayer.getEsriGraphic('overview-graphic')!.geometry!
                    ) as EsriExtent;
                }
            } else if (this.startExtent) {
                // determine delta in map coords from drag origin to current drag point and update extent graphic
                const origin = this.esriView!.toMap(esriDrag.origin);
                const pos = this.esriView!.toMap({
                    x: esriDrag.x,
                    y: esriDrag.y
                });
                const newExtent = this.startExtent.clone().offset(pos.x - origin.x, pos.y - origin.y, 0);
                this.overviewGraphicLayer.getEsriGraphic('overview-graphic')!.geometry = newExtent;

                if (esriDrag.action === 'end') {
                    // zoom main map once drag is done
                    this.$iApi.geo.map.zoomMapTo(this.$iApi.geo.geom.geomEsriToRamp(newExtent), undefined, false);
                    this.startExtent = null;
                }
            }
        }
    }

    /**
     * Updates overviewmap extent and graphic based on main map extent
     *
     * @param {Extent} newExtent new main map extent
     * @returns {Promise<void>} A promise that resolves when the overviewmap has finished updating
     */
    updateOverview(newExtent: Extent): Promise<void> {
        const expandFactor: number = this.overviewmapStore.expandFactor;

        const zoomPromise = this.zoomMapTo(newExtent.expand(expandFactor), undefined, false);

        const graphic = this.overviewGraphicLayer.getLocalGraphic('overview-graphic');

        // Instead of directly changing graphic geometry, we need to remove graphic, change geometry, and re add graphic.
        // This is because of a glitch where changing the geometry directly sometimes causes multiple indicator rectangles
        // to appear in the overview map (https://github.com/ramp4-pcar4/ramp4-pcar4/issues/1493).
        // We suspect that this is an ESRI bug, and are implementing this hack as a workaround for now.
        this.overviewGraphicLayer.removeGraphic(graphic);
        graphic!.geometry = newExtent;
        this.overviewGraphicLayer.addGraphic(graphic!);

        return zoomPromise;
    }

    /**
     * Checks if mouse event or screen point intersects with extent graphic
     *
     * @param {MouseEvent | EsriScreenPoint} e
     * @returns {Promise<boolean>}
     */
    async cursorHitTest(e: MouseEvent | EsriScreenPoint): Promise<boolean> {
        const hitTestResult = await this.esriView!.hitTest(e);
        return hitTestResult.results.length > 0;
    }
}
